import React, { useState } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { goalsMapToList } from '../../services/funnelService';

const Container = styled.div`
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px);
  padding-bottom: calc(
    ${theme.spacing.screenPadding} + 140px + env(safe-area-inset-bottom, 0px)
  );
  overflow-x: hidden;
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-bottom: calc(
      ${theme.spacing.screenPadding} + 180px + env(safe-area-inset-bottom, 0px)
    );
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  text-align: center;
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  flex: 0 0 auto;
`;

const ProgressBarContainer = styled.div`
  width: 50vw;
  max-width: 280px;
  margin: 0 auto 32px auto;
  height: 4px;
  background: ${theme.colors.doryLighterBlue};
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${(props) => (props.progress / props.total) * 100}%;
  background: ${theme.colors.doryGreen};
  border-radius: 2px;
  transition: width ${theme.animation.durations.normal};
`;

const GoalsList = styled.ul`
  list-style: none;
  width: 100%;
  margin: ${theme.spacing.xl} 0 ${theme.spacing.xl} 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  text-align: left;
`;

const GoalItem = styled.li`
  width: 100%;
  margin: 0;
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid rgba(64, 92, 115, 0.16);
  background: rgba(242, 248, 247, 0.6);
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.bodyLarge};
  font-weight: ${theme.typography.weights.medium};
  line-height: ${theme.typography.lineHeights.relaxed};
  color: ${theme.colors.text};
`;

const BodyCopy = styled.p`
  width: 100%;
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.regular};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.md} 0;
  line-height: ${theme.typography.lineHeights.relaxed};
  text-align: left;

  &:last-of-type {
    margin-bottom: ${theme.spacing.lg};
  }
`;

const Title = styled.h1`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.md} 0;
  line-height: 1.1;
  text-align: left;
  width: 100%;
`;

const ChoiceRow = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  width: 100%;
  margin-top: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.xl};
  flex-shrink: 0;
`;

const PrimaryButton = styled.button`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.bodyLarge};
  font-weight: ${theme.typography.weights.semiBold};
  min-height: 52px;
  width: 100%;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  background: linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent});
  color: #fff;
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: 56px;
  }
`;

const SecondaryButton = styled.button`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.bodyLarge};
  font-weight: ${theme.typography.weights.semiBold};
  min-height: 52px;
  width: 100%;
  border: 2px solid ${theme.colors.doryBlue};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  background: #fff;
  color: ${theme.colors.doryBlue};
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  &:hover:not(:disabled) {
    background: ${theme.colors.doryBlue};
    color: #fff;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: 56px;
  }
`;

const ErrorText = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: #8b4a4a;
  margin: 0 0 ${theme.spacing.md} 0;
`;

const getFirstName = (userProgress) => {
  const name = userProgress?.userInfo?.name;
  if (typeof name !== 'string' || !name.trim()) return '';
  return name.trim().split(/\s+/)[0];
};

const getTopGoals = (userProgress) =>
  goalsMapToList(userProgress?.userInfo?.goals);

const CommitmentScreen = ({ data, onResponse, userProgress = {} }) => {
  const firstName = getFirstName(userProgress);
  const topGoals = getTopGoals(userProgress);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const goalWord = topGoals.length === 1 ? 'goal' : 'goals';
  const protocolCopy = `The Dory Protocol helps you to achieve your ${goalWord} faster by constantly supporting the strength of your mindstate.`;
  const flowCopy =
    'Everything flows from taking one minute to check in with Dory on your phone.';

  const question = firstName
    ? `${firstName}, in order to achieve your ${goalWord} faster, are you willing to commit to checking in with Dory at least five days a week?`
    : `In order to hit your ${goalWord} faster, are you willing to commit to checking in with Dory at least five days a week?`;

  const finishAndSubmit = async (committed) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError('');

    const responseData = {
      checkpointKey: data.key,
      type: 'commitment',
      committed,
      selectedOption: committed ? 1 : 0,
      selectedOptionText: committed ? 'Yes' : 'No',
      timestamp: Date.now(),
    };

    try {
      await onResponse?.(responseData);
    } catch (err) {
      console.error('Failed to save commitment response:', err);
      setError('Failed to save your response. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <Content>
        <ProgressBarContainer>
          <ProgressFill progress={1} total={1} />
        </ProgressBarContainer>

        <BodyCopy>{protocolCopy}</BodyCopy>
        <BodyCopy>{flowCopy}</BodyCopy>

        <Title>{question}</Title>

        {topGoals.length > 0 && (
          <GoalsList aria-label="Your goals">
            {topGoals.map((goal, index) => (
              <GoalItem key={`commitment-goal-${index}`}>{goal}</GoalItem>
            ))}
          </GoalsList>
        )}

        {error && <ErrorText>{error}</ErrorText>}

        <ChoiceRow>
          <PrimaryButton
            onClick={() => finishAndSubmit(true)}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving…' : 'Yes'}
          </PrimaryButton>
          <SecondaryButton
            onClick={() => finishAndSubmit(false)}
            disabled={isSubmitting}
          >
            No
          </SecondaryButton>
        </ChoiceRow>
      </Content>
    </Container>
  );
};

export default CommitmentScreen;
